import type { OnboardingStatus } from "@/features/organizations/onboarding.api";

export type SetupChecklistItem = {
  id: "org" | "workspace" | "invite";
  label: string;
  done: boolean;
};

export type SetupChecklistInviteEvidence = {
  /**
   * How many people are in the organization, signed-in Admin included. null
   * means the roster could not be read — "unknown", never "just you".
   */
  orgMemberCount?: number | null;
};

/** The organization has a team once it holds at least this many members. */
export const TEAM_MEMBER_THRESHOLD = 2;

/**
 * Derive setup checklist rows from real onboarding-status fields plus live
 * roster evidence. "Invite teammates" is complete when the organization has at
 * least two members. A pending invitation is not a member, and the wizard
 * having visited the step is not a team, so neither completes it. An unreadable
 * roster leaves it incomplete (unknown is not done).
 */
export function deriveSetupChecklist(
  status: OnboardingStatus,
  inviteEvidence: SetupChecklistInviteEvidence = {},
): SetupChecklistItem[] {
  const completed = Array.isArray(status.completedSteps) ? status.completedSteps : [];
  const hasWorkspace = Number(status.workspaceCount ?? 0) > 0;
  const memberCount = inviteEvidence.orgMemberCount;
  const inviteDone =
    typeof memberCount === "number" && memberCount >= TEAM_MEMBER_THRESHOLD;

  return [
    {
      id: "org",
      label: "Confirm organization",
      done:
        completed.includes("org") ||
        status.completed === true ||
        status.skipped === true ||
        hasWorkspace,
    },
    {
      id: "workspace",
      label: "Create first workspace",
      done: hasWorkspace || completed.includes("workspace"),
    },
    {
      id: "invite",
      label: "Invite teammates",
      done: inviteDone,
    },
  ];
}
